import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase';
import type { Session, User } from '@supabase/supabase-js';
import { fetchUserBadgesWithDetails, computeHighestBadgeScore } from './badges';

export interface UserSettings {
  display_name: string | null;
  profile_name: string | null;
  display_profile: boolean | null;
  profile_bio: string | null;
  profile_picture_name: string | null;
  profile_picture_generated_at: string | null;
}

interface BadgeFeatureRow {
  feature?: {
    key?: string | null;
  } | null;
}

async function fetchFeatureKeysForBadgeIds(
  badgeIds: string[]
): Promise<string[]> {
  if (!badgeIds || badgeIds.length === 0) return [];

  const { data, error } = await supabase
    .from('badge_features')
    .select('feature:features(key)')
    .in('badge_id', badgeIds);

  if (error) {
    throw error;
  }

  const keys = new Set<string>();
  (data as BadgeFeatureRow[] | null | undefined)?.forEach((row) => {
    const key = row?.feature?.key;
    if (key) keys.add(key);
  });

  return Array.from(keys);
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [user_settings, setUserSettings] = useState<UserSettings | null>(null);
  const [highestBadgeScore, setHighestBadgeScore] = useState<number>(0);
  const [userFeatures, setUserFeatures] = useState<string[]>([]);

  const fetchBadgeDataForUser = useCallback(
    async (targetUser: User | null): Promise<{
      highestScore: number;
      featureKeys: string[];
    }> => {
      if (!targetUser) {
        return { highestScore: 0, featureKeys: [] };
      }

      try {
        const userBadges = await fetchUserBadgesWithDetails(targetUser.id);
        const highestScore = computeHighestBadgeScore(userBadges);
        const badgeIds = userBadges
          .map((badge) => badge.badge_id)
          .filter((id): id is string => Boolean(id));
        try {
          const featureKeys = await fetchFeatureKeysForBadgeIds(badgeIds);
          return { highestScore, featureKeys };
        } catch (featureErr) {
          console.debug('Failed to load user features', featureErr);
          return { highestScore, featureKeys: [] };
        }
      } catch (err) {
        console.debug('Failed to compute highest badge score', err);
        return { highestScore: 0, featureKeys: [] };
      }
    },
    []
  );

  const refreshUserFeaturesForUser = useCallback(
    async (targetUser: User | null) => {
      const badgeData = await fetchBadgeDataForUser(targetUser);
      setHighestBadgeScore(badgeData.highestScore);
      setUserFeatures(badgeData.featureKeys);
    },
    [fetchBadgeDataForUser]
  );

  const refreshUserFeatures = useCallback(async () => {
    await refreshUserFeaturesForUser(user);
  }, [user, refreshUserFeaturesForUser]);

  useEffect(() => {
    let mounted = true;

    async function fetchSettings(u: User | null) {
      if (!u) {
        if (!mounted) return;
        setUserSettings(null);
        setUserFeatures([]);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_settings')
          .select('*')
          .eq('user_id', u.id)
          .limit(1)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error(error);
        }

        if (!mounted) return;
        setUserSettings((data ?? null) as UserSettings | null);

        const badgeData = await fetchBadgeDataForUser(u);
        if (!mounted) return;
        setHighestBadgeScore(badgeData.highestScore);
        setUserFeatures(badgeData.featureKeys);
      } catch (err) {
        console.error('Failed to load user settings', err);
        if (!mounted) return;
        setUserSettings(null);
        setUserFeatures([]);
      }
    }

    async function init() {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!mounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      await fetchSettings(session?.user ?? null);
      setLoading(false);
    }

    const sub = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      fetchSettings(session?.user ?? null);
    });

    init();
    return () => {
      mounted = false;
      sub.data.subscription.unsubscribe();
    };
  }, [fetchBadgeDataForUser]);

  return {
    user,
    session,
    loading,
    user_settings,
    highestBadgeScore,
    userFeatures,
    refreshUserFeatures,
  };
}
